import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { friendlyUploadError } from '../lib/errors';
import { compressImage } from '../lib/imageCompression';
import { uploadWithProgress } from '../lib/uploadWithProgress';

const RecruiterSignupPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company_name: '',
  });
  const [businessLicense, setBusinessLicense] = useState(null);
  const [idProof, setIdProof] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!businessLicense || !idProof) {
      setError('Please upload both Business License and ID Proof');
      setLoading(false);
      return;
    }

    try {
      // Compress image uploads to ~300 KB each; PDFs pass through unchanged.
      setProgress('compressing');
      const [licenseCompressed, idCompressed] = await Promise.all([
        compressImage(businessLicense),
        compressImage(idProof),
      ]);
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('company_name', formData.company_name);
      formDataToSend.append('business_license', licenseCompressed);
      formDataToSend.append('id_proof', idCompressed);

      const response = await uploadWithProgress(
        `${process.env.REACT_APP_BACKEND_URL}/api/recruiter/signup`,
        { method: 'POST', body: formDataToSend, onProgress: ({ pct }) => setProgress(pct ?? 'uploading') }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Signup failed');
      }
      setSuccess(true);
    } catch (err) {
      setError(friendlyUploadError(err, 'An error occurred during signup'));
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-grow flex items-center justify-center bg-tge-paper px-4 pt-[120px] pb-16">
          <Card className="w-full max-w-md rounded-3xl shadow-[0_2px_30px_rgba(27,42,107,0.08)]">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-tge-gold/15 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-tge-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <CardTitle className="font-serif-display text-3xl text-tge-navy">Application Submitted</CardTitle>
              <CardDescription className="text-base mt-2 text-tge-muted">
                Thank you for your interest in partnering with True Global Education.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-tge-muted text-center">
                Your application is now under review by our admin team. You will receive an agreement via email once your application is approved.
              </p>
              <div className="flex flex-col gap-2">
                <Button onClick={() => navigate('/')} className="w-full bg-tge-navy hover:bg-tge-navy-deep text-white rounded-full">
                  Return to Home
                </Button>
                <Button variant="outline" onClick={() => navigate('/login')} className="w-full rounded-full">
                  Go to Login
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-grow bg-tge-paper px-4 pt-[120px] pb-16">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="font-serif-display text-4xl text-tge-navy text-center">Recruiter Partner Signup</CardTitle>
              <CardDescription className="text-center text-base">
                Join True Global Education as a recruitment partner
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@company.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input
                    id="company_name"
                    type="text"
                    required
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="Your Company Ltd."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="business_license">Business License *</Label>
                  <Input
                    id="business_license"
                    type="file"
                    required
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setBusinessLicense(e.target.files[0])}
                  />
                  <p className="text-xs text-gray-500">Accepted formats: PDF, JPG, PNG (Max 10MB)</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="id_proof">ID Proof (Passport/Driver's License) *</Label>
                  <Input
                    id="id_proof"
                    type="file"
                    required
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setIdProof(e.target.files[0])}
                  />
                  <p className="text-xs text-gray-500">Accepted formats: PDF, JPG, PNG (Max 10MB)</p>
                </div>

                <Button type="submit" className="w-full" size="lg" disabled={loading}>
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    'Submit Application'
                  )}
                </Button>
                {loading && (
                  <div data-testid="recruiter-signup-progress">
                    <div className="flex justify-between text-[11px] text-gray-500 mb-1">
                      <span>
                        {progress === 'compressing' && 'Optimising documents…'}
                        {progress === 'uploading' && 'Uploading…'}
                        {typeof progress === 'number' && `Uploading… ${progress}%`}
                        {progress == null && 'Preparing…'}
                      </span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-tge-gold transition-all duration-300"
                        style={{ width: typeof progress === 'number' ? `${progress}%` : '10%' }}
                      />
                    </div>
                  </div>
                )}

                <p className="text-center text-sm text-gray-600">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-blue-600 hover:underline font-semibold"
                  >
                    Login here
                  </button>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RecruiterSignupPage;
